import React, { useEffect, useMemo, useState } from 'react'
import styles from './MonthRangeCalendar.module.css'

const MONTHS = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
  'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc',
]

const parseMonthValue = (value) => {
  if (!value) return null
  const [year, month] = value.split('-').map(Number)
  if (!year || !month) return null
  return { year, month }
}

const toKey = (year, month) => year * 12 + month

const yearOptions = (centerYear) => {
  const list = []
  for (let y = centerYear - 8; y <= centerYear + 8; y += 1) list.push(y)
  return list
}

const MonthGrid = ({
  viewYear,
  onViewYearChange,
  startValue,
  endValue,
  mode,
  onSelectMonth,
}) => {
  const start = parseMonthValue(startValue)
  const end = parseMonthValue(endValue)
  const startKey = start ? toKey(start.year, start.month) : null
  const endKey = end ? toKey(end.year, end.month) : null
  const rangeMin = startKey != null && endKey != null ? Math.min(startKey, endKey) : null
  const rangeMax = startKey != null && endKey != null ? Math.max(startKey, endKey) : null
  const years = useMemo(() => yearOptions(viewYear), [viewYear])

  return (
    <>
      <div className={styles.calendarHeader}>
        <button
          type="button"
          className={styles.navBtn}
          onClick={() => onViewYearChange(viewYear - 1)}
          aria-label="Année précédente"
        >
          ‹
        </button>
        <select
          className={styles.yearSelect}
          value={viewYear}
          onChange={(e) => onViewYearChange(Number(e.target.value))}
          aria-label="Année"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button
          type="button"
          className={styles.navBtn}
          onClick={() => onViewYearChange(viewYear + 1)}
          aria-label="Année suivante"
        >
          ›
        </button>
      </div>

      <div className={styles.monthGrid}>
        {MONTHS.map((label, index) => {
          const month = index + 1
          const key = toKey(viewYear, month)
          const isStart = start?.year === viewYear && start?.month === month
          const isEnd = end?.year === viewYear && end?.month === month
          const inRange = mode === 'end'
            && rangeMin != null
            && rangeMax != null
            && key >= rangeMin
            && key <= rangeMax
          const disabled = mode === 'end' && startKey != null && key < startKey
          const isStartOnly = mode === 'start' && isStart

          return (
            <button
              key={label}
              type="button"
              disabled={disabled}
              className={[
                styles.monthCell,
                inRange ? styles.monthInRange : '',
                isStartOnly ? styles.monthStart : '',
                mode === 'end' && isStart ? styles.monthStart : '',
                mode === 'end' && isEnd ? styles.monthEnd : '',
                disabled ? styles.monthDisabled : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onSelectMonth(viewYear, month)}
            >
              {label}
            </button>
          )
        })}
      </div>
    </>
  )
}

const MonthRangeCalendar = ({
  startValue,
  endValue,
  onSelectStart,
  onSelectEnd,
}) => {
  const start = parseMonthValue(startValue)
  const end = parseMonthValue(endValue)
  const now = new Date().getFullYear()

  const [startViewYear, setStartViewYear] = useState(start?.year || end?.year || now)
  const [endViewYear, setEndViewYear] = useState(end?.year || start?.year || now)

  useEffect(() => {
    if (start?.year) setStartViewYear(start.year)
  }, [start?.year])

  useEffect(() => {
    if (end?.year) setEndViewYear(end.year)
    else if (start?.year) setEndViewYear(start.year)
  }, [end?.year, start?.year])

  const showEndCalendar = Boolean(startValue)

  return (
    <div className={styles.dualCalendar} role="group" aria-label="Sélection période début et fin">
      <div className={`${styles.calendarPane} ${styles.calendarPaneStart}`}>
        <div className={styles.paneHeader}>
          <span className={styles.paneStep}>1</span>
          <div>
            <strong>Date de début</strong>
            <p>{startValue || 'Choisissez un mois'}</p>
          </div>
        </div>
        <MonthGrid
          viewYear={startViewYear}
          onViewYearChange={setStartViewYear}
          startValue={startValue}
          endValue={endValue}
          mode="start"
          onSelectMonth={onSelectStart}
        />
      </div>

      <div className={styles.calendarDivider} aria-hidden="true" />

      {showEndCalendar ? (
        <div className={`${styles.calendarPane} ${styles.calendarPaneEnd}`}>
          <div className={styles.paneHeader}>
            <span className={styles.paneStep}>2</span>
            <div>
              <strong>Date de fin</strong>
              <p>{endValue || 'Choisissez le mois de fin'}</p>
            </div>
          </div>
          <MonthGrid
            viewYear={endViewYear}
            onViewYearChange={setEndViewYear}
            startValue={startValue}
            endValue={endValue}
            mode="end"
            onSelectMonth={onSelectEnd}
          />
        </div>
      ) : (
        <div className={`${styles.calendarPane} ${styles.calendarPanePlaceholder}`}>
          <div className={styles.placeholderInner}>
            <span className={styles.placeholderIcon}>→</span>
            <strong>Date de fin</strong>
            <p>Sélectionnez d&apos;abord le mois de début à gauche.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default MonthRangeCalendar
